import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import {
  type AdminRole,
  BackfillAggregationsInput,
  type BackfillAggregationsOutput,
  BroadcastToOwnersInput,
  CancelInvoiceInput,
  CancelSubscriptionAdminInput,
  CategorizeChurnReasonInputSchema,
  PostponeRenewalAdminInput,
  CreateAddonInputSchema,
  CreateApiKeyInputSchema,
  type CreateApiKeyResult,
  CreateCourtesyTenantInput,
  CreateLabelTemplateInput,
  CreateResponsibleInputSchema,
  CreateStaffWithCredentialsInput,
  RevokeApiKeyInputSchema,
  DeleteApiKeyInputSchema,
  RotateApiKeySecretInputSchema,
  type RotateApiKeySecretResult,
  UpdateApiKeyInputSchema,
  DeleteLabelTemplateInput,
  DeleteResponsibleInputSchema,
  HardDeleteEmptyTenantInputSchema,
  InviteUserToTenantInput,
  SetMemberPasswordInput,
  SetResponsibleActiveInputSchema,
  SetResponsibleDefaultInputSchema,
  SetStaffPinInput,
  UpdateUserTenantRoleInput,
  RemoveUserFromTenantInput,
  DisableAddonInputSchema,
  EnableAddonInputSchema,
  ExportAuditBillingInputSchema,
  ReactivateTenantInput,
  RecordManualPaymentInput,
  RegisterAdminPushTokenInput,
  RemoveSuperAdminInput,
  ListAdminUsersInput,
  type ListAdminUsersResult,
  ApplyLifetimeFreeInput,
  RemoveLifetimeFreeInput,
  RequestRefundInput,
  RequestTenantDeletionInputSchema,
  ResendInvoiceEmailInput,
  GetInvoiceDocumentUrlInput,
  type GetInvoiceDocumentUrlOutput,
  RestoreTenantInputSchema,
  SearchTenantsInputSchema,
  type SearchTenantsResult,
  SearchPlatformUsersInputSchema,
  type SearchPlatformUsersResult,
  AdminSetContactVerificationInput,
  AdminSetUserAccountStateInput,
  AdminRevokeUserSessionsInput,
  SetAddonEnabledCountriesInputSchema,
  SetMarketConfigInput,
  SetRegionsConfigInput,
  type RegionsConfig,
  UpsertSellerEntityInputSchema,
  AsaasFiscalInfoModeInputSchema,
  AsaasMunicipalServicesInputSchema,
  type SellerEntity,
  SetPspConfigInput,
  DeletePspConfigInput,
  SeedDefaultLabelTemplatesForExistingTenantInput,
  SetDefaultLabelTemplateInput,
  SetSuperAdminInput,
  SetTenantDemoFlagInput,
  SuspendTenantInput,
  UnregisterAdminPushTokenInput,
  UpdateAddonInputSchema,
  type UpdateAddonOutput,
  UpdateBillingSettingsInput,
  UpdateContactSubmissionStatusInputSchema,
  UpdateLabelTemplateInput,
  CreateFinanceAccountInputSchema,
  UpdateFinanceAccountInputSchema,
  ArchiveFinanceAccountInputSchema,
  type CreateFinanceAccountOutput,
  type UpdateFinanceAccountOutput,
  type ArchiveFinanceAccountOutput,
  CreateFinanceEntryInputSchema,
  UpdateFinanceEntryInputSchema,
  DeleteFinanceEntryInputSchema,
  SearchFinanceEntriesInputSchema,
  type CreateFinanceEntryOutput,
  type UpdateFinanceEntryOutput,
  type DeleteFinanceEntryOutput,
  type SearchFinanceEntriesResult,
  ListFinanceTagsInputSchema,
  type ListFinanceTagsResult,
  ComputeFinanceBalanceInputSchema,
  type ComputeFinanceBalanceResult,
  ComputeCashflowInputSchema,
  type ComputeCashflowResult,
  ComputeSubscriptionReceivablesInputSchema,
  type ComputeSubscriptionReceivablesResult,
  ParseBankStatementImageInputSchema,
  ConfirmStatementImportInputSchema,
  type ParseBankStatementImageOutput,
  type ConfirmStatementImportOutput,
  AdminCreateCheckoutLinkInput,
  AdminGetCheckoutLinksInput,
  AdminSendCheckoutLinkEmailInput,
  type AdminCreateCheckoutLinkOutput,
  type AdminGetCheckoutLinksOutput,
  type AdminSendCheckoutLinkEmailOutput,
  SetAppVersionConfigInputSchema,
  SetTermsVersionInput,
  type SetTermsVersionResult,
  MigrateCourtesyInput,
  RestoreLegacyCourtesyInput,
  ChangeTenantCommercialModeInput,
  CancelCommercialModeTransitionInput,
  type ChangeTenantCommercialModeOutput,
} from '@etiquetachef/types';
import { z } from 'zod';
import { functions } from './firebase';
import { reportNonFatal, serializeError } from './errorReport';
import { assertOnline } from '@etiquetachef/ui-shared/online';

const TAG = 'admin/callables';

/** Erros que `reportCallableFailure` já mandou ao PostHog (WeakSet: não segura o erro na memória). */
const REPORTED_BY_WRAPPER = new WeakSet();

/**
 * Reporta ao PostHog a falha de uma callable do admin.
 *
 * Origem: #1021 — 2026-10-23 (auditoria de erros invisíveis).
 *
 * Por quê: o wrapper do `apps/app` já reportava `callable_failed` (apps/app/src/lib/callables.ts);
 * o do admin só fazia `console.error`, e sem DevTools do cliente cada falha de callable do
 * super-admin (reembolso, suspensão, criação de tenant, termos…) era invisível — mesmo desenho
 * que o #1020 corrigiu no iOS. Aqui fecha de uma vez todas as telas que chamam `…Callable`.
 *
 * Contrato: `callable` é o nome da Function; `code` é o `FirebaseError.code` (`functions/…`) ou
 * `null`. Nunca lança e NÃO inclui o input (pode ter dado pessoal ou segredo de PSP). Erros de
 * fluxo esperados (`functions/failed-precondition`, `already-exists`) também saem — o `code`
 * deixa filtrar no painel; sem report o chamador teria de lembrar de reportar em cada tela.
 *
 * Usado por: o wrapper `callable()`, `listDrilldownCallable` e `verifyAdminLoginCallable`, logo
 * por todo `apps/admin` que importa `lib/callables`.
 *
 * Histórico:
 *  - 2026-10-25 15:52 +0100 — (#1021) marca o erro reportado (`wasReportedByCallable`).
 */
function reportCallableFailure(name: string, err: unknown): void {
  if (typeof err === 'object' && err !== null) REPORTED_BY_WRAPPER.add(err);
  reportNonFatal('callable_failed', {
    callable: name,
    code: (err as { code?: string }).code ?? null,
    ...serializeError(err),
  });
}

/**
 * `true` se a falha veio da callable e o wrapper já a reportou como `callable_failed`.
 *
 * Origem: #1021 — 2026-10-25 15:52 +0100 (2ª auditoria).
 *
 * Por quê: os `catch` das telas que ganharam `reportNonFatal` próprio na #1021 dobravam o evento —
 * dois `$exception` por falha no Error Tracking. O report local continua valendo para o que o
 * wrapper não vê: `ZodError` do input e o `assertOnline`, lançados ANTES do `try` dele.
 *
 * Contrato: puro; `false` para valor que não é objeto.
 *
 * Usado por: `AccountActionDialog`, `CreateCourtesyTenantDialog`, `ApiKeysPage` (rotate),
 * `AppVersionGatePage`, `FinanceEntriesPage` (excluir) — admin web.
 */
export function wasReportedByCallable(err: unknown): boolean {
  return typeof err === 'object' && err !== null && REPORTED_BY_WRAPPER.has(err);
}

/**
 * Wrapper genérico das callables do admin: valida o input com Zod no cliente
 * antes de mandar para a Function (o servidor revalida — defesa em
 * profundidade) e loga invoked/success/failed para diagnosticar via console
 * do navegador.
 *
 * Origem: sem issue — commit efa5a8ca2, 2026-04-26 (Sprint 2, aba Billing
 * editável).
 *
 * Por quê: o schema do input mora em `@etiquetachef/types` e é o MESMO que a
 * Function usa no `parse` — validar aqui devolve o erro de formulário sem ida
 * à rede, e um campo renomeado quebra o build do admin em vez de virar
 * `invalid-argument` em produção.
 *
 * Contrato: devolve `(data) => Promise<O>`. Lança `ZodError` (input inválido,
 * nada é enviado) ou o `FirebaseError` da callable (`functions/permission-
 * denied`, `invalid-argument`, `failed-precondition`…), que o chamador traduz
 * para toast. A resposta NÃO é validada aqui — `O` é só tipo; quem precisa de
 * garantia faz `safeParse` no consumidor (ex.: `useAdminUsers`).
 *
 * Usado por: todas as `export const …Callable` deste arquivo, consumidas por
 * ~69 módulos do `apps/admin` (`rg "lib/callables'" apps/admin/src`) — telas
 * de `routes/`, diálogos de `features/**` e `auth/AuthProvider.tsx`.
 *
 * Arquitetura: autorização é do servidor — as callables do admin exigem o
 * claim `superAdmin` (`ADMIN_CALLABLE_OPTIONS` e
 * `apps/functions/src/auth/permissions.ts`). O cliente só formata.
 *
 * Dicas: o log não inclui o input de propósito — vários carregam dado pessoal
 * ou segredo (credencial de PSP vive em `lib/pspCallables.ts` pelo mesmo
 * motivo). Callable nova: schema em `packages/types`, wrapper aqui, e a
 * Function com o mesmo schema.
 *
 * Falhas conhecidas:
 *  - até 2026-10-23 (#1021): a falha só ia para `console.error` — invisível em
 *    produção (sem DevTools do cliente). Agora vai ao PostHog por
 *    `reportCallableFailure`, como o wrapper do `apps/app`.
 *
 * Issues: #1021 (report central das callables do admin).
 *
 * Histórico:
 *  - 2026-10-23 — (#1021) `callable_failed` no PostHog em vez de só console.
 */
function callable<I extends z.ZodType, O = unknown>(
  name: string,
  input: I,
): (data: z.input<I>) => Promise<O> {
  const fn: HttpsCallable<z.output<I>, O> = httpsCallable(functions, name);
  return async (data) => {
    // Web sempre online (#1010): offline confirmado recusa já, com erro tipado.
    assertOnline();
    const parsed = input.parse(data);
    console.info(`${TAG}/${name} invoked`);
    try {
      const result = await fn(parsed);
      console.info(`${TAG}/${name} success`);
      return result.data;
    } catch (err) {
      reportCallableFailure(name, err);
      throw err;
    }
  };
}

export const setAppVersionConfigCallable = callable(
  'setAppVersionConfig',
  SetAppVersionConfigInputSchema,
);
export const setTermsVersionCallable = callable<typeof SetTermsVersionInput, SetTermsVersionResult>(
  'setTermsVersion',
  SetTermsVersionInput,
);
export const suspendTenantCallable = callable('suspendTenant', SuspendTenantInput);
export const reactivateTenantCallable = callable('reactivateTenant', ReactivateTenantInput);
export const setTenantDemoFlagCallable = callable('setTenantDemoFlag', SetTenantDemoFlagInput);
export const requestTenantDeletionCallable = callable(
  'requestTenantDeletion',
  RequestTenantDeletionInputSchema,
);
export const restoreTenantCallable = callable('restoreTenant', RestoreTenantInputSchema);

/** Busca server-side da lista de Empresas (PropertyFilter → tokens, issue #71). */
export const searchTenantsCallable = callable<typeof SearchTenantsInputSchema, SearchTenantsResult>(
  'searchTenants',
  SearchTenantsInputSchema,
);

/**
 * Issue #257 — tela global de Usuários (Empresas → Usuários). Uma linha por
 * pessoa, cross-tenant. Precisa ser callable: as rules não sancionam listagem
 * de `/profiles` nem `collectionGroup('profiles')`, e o estado real de
 * `emailVerified` só existe no Firebase Auth.
 */
export const searchPlatformUsersCallable = callable<
  typeof SearchPlatformUsersInputSchema,
  SearchPlatformUsersResult
>('searchPlatformUsers', SearchPlatformUsersInputSchema);

/** Carimba/revoga a verificação de contato de um usuário (e-mail ou telefone). */
export const adminSetContactVerificationCallable = callable<
  typeof AdminSetContactVerificationInput,
  { changed: true; sessionsRevoked: boolean }
>('adminSetContactVerification', AdminSetContactVerificationInput);

/** Bloqueia/desbloqueia a conta de login no Firebase Auth (`disabled`). */
export const adminSetUserAccountStateCallable = callable<
  typeof AdminSetUserAccountStateInput,
  { changed: true }
>('adminSetUserAccountState', AdminSetUserAccountStateInput);

/** Derruba o usuário de todos os aparelhos (`revokeRefreshTokens`). */
export const adminRevokeUserSessionsCallable = callable<
  typeof AdminRevokeUserSessionsInput,
  { revoked: true }
>('adminRevokeUserSessions', AdminRevokeUserSessionsInput);

/**
 * Issue #120 — cria tenant de cortesia tendo o super-admin logado como OWNER.
 * Desde a F7 da #679 ele já nasce `commercial_mode: COURTESY`, sem Stripe —
 * é o tipo comercial que libera o gate; não há segundo callable.
 */
export const createCourtesyTenantCallable = callable<
  typeof CreateCourtesyTenantInput,
  { tenantId: string }
>('createCourtesyTenant', CreateCourtesyTenantInput);

// Sprint 40 — gate AUTORITATIVO de tax-id (owner 2026-06-11: tax-id valida
// SEMPRE via Cloud Function). Input definido inline nas Functions
// (`validateTaxId.ts`), não exportado em types — espelhamos, como faz
// `apps/app/src/lib/callables.ts`.
const ValidateTaxIdInput = z.object({
  country: z.string().regex(/^[A-Z]{2}$/),
  taxId: z.string().max(60),
});
export const validateTaxIdCallable = callable<typeof ValidateTaxIdInput, { valid: boolean }>(
  'validateTaxId',
  ValidateTaxIdInput,
);

/** Counts devolvidos pela callable quando o tenant NÃO está vazio (bloqueio). */
export type HardDeleteTenantCounts = {
  products: number;
  labels: number;
  payments: number;
};
export const hardDeleteEmptyTenantCallable = callable<
  typeof HardDeleteEmptyTenantInputSchema,
  { tenantId: string; deleted: boolean; counts: HardDeleteTenantCounts }
>('hardDeleteEmptyTenant', HardDeleteEmptyTenantInputSchema);
// ROOT-only desde a #795 (ADM-2): eram abertas a qualquer papel de admin.
export const setSuperAdminCallable = callable('setSuperAdmin', SetSuperAdminInput);
export const removeSuperAdminCallable = callable('removeSuperAdmin', RemoveSuperAdminInput);
/**
 * #795 (ADM-6) — quem tem acesso admin, lido do Firebase Auth (ROOT-only). A
 * tela `/settings/super-admins` montava essa lista das últimas 100 entradas de
 * `/audit` e perdia admins antigos.
 */
export const listAdminUsersCallable = callable<typeof ListAdminUsersInput, ListAdminUsersResult>(
  'listAdminUsers',
  ListAdminUsersInput,
);

// Contato da landing — triagem de leads (NEW/READ/ARCHIVED).
export const updateContactSubmissionStatusCallable = callable(
  'updateContactSubmissionStatus',
  UpdateContactSubmissionStatusInputSchema,
);

// Billing — Sprint 16
export const recordManualPaymentCallable = callable(
  'recordManualPayment',
  RecordManualPaymentInput,
);
export const cancelInvoiceCallable = callable('cancelInvoice', CancelInvoiceInput);
export const resendInvoiceEmailCallable = callable(
  'resendInvoiceEmail',
  ResendInvoiceEmailInput,
);
// #776 — link ATUAL do PDF da fatura (o gravado pelo webhook do Stripe expira).
export const getInvoiceDocumentUrlCallable = callable<
  typeof GetInvoiceDocumentUrlInput,
  GetInvoiceDocumentUrlOutput
>('getInvoiceDocumentUrl', GetInvoiceDocumentUrlInput);
export const updateBillingSettingsCallable = callable(
  'updateBillingSettings',
  UpdateBillingSettingsInput,
);
// 2026-06-08: preço é editado SÓ no PSP (Stripe) e puxado por syncPlanPrices;
// os callables de edição (`updatePlanPrice`/`updatePlanPriceForSku`) foram removidos.
// Sprint 35 PR G5 — habilita/desabilita mercado por país com Guards
// 6/7/8 (UE→Paddle, IT→Paddle MoR, não-UE→sem Paddle).
/**
 * Resposta do `setMarketConfig`. `payment_mode` só vem do servidor da #756 em
 * diante — ausente = servidor antigo, que descartou o modo (ver `asaas.md`).
 */
export type SetMarketConfigOutput = {
  ok: boolean;
  country: string;
  saleable: boolean;
  payment_mode?: 'live' | 'sandbox' | null;
};
export const setMarketConfigCallable = callable<typeof SetMarketConfigInput, SetMarketConfigOutput>(
  'setMarketConfig',
  SetMarketConfigInput,
);
// #1078 (F2 da #960) — chave `config/regions.europeWest1Enabled` (ROOT; a Function recusa ligar
// com `failed-precondition` + `details.reason: 'region_not_enabled'` antes da F4). Usado por
// `features/countries/RegionsSwitch.tsx`.
export const setRegionsConfigCallable = callable<typeof SetRegionsConfigInput, RegionsConfig>(
  'setRegionsConfig',
  SetRegionsConfigInput,
);
// #756 (A7) — entidades vendedoras e o cadastro fiscal da conta do Asaas.
export const adminUpsertSellerEntityCallable = callable<typeof UpsertSellerEntityInputSchema, SellerEntity>(
  'adminUpsertSellerEntity',
  UpsertSellerEntityInputSchema,
);
/** Resposta de `adminGetAsaasFiscalInfo`/`adminSyncAsaasFiscalInfo` (`apps/functions/src/admin/sellerEntities.ts`). */
export type AsaasFiscalStatus = {
  mode: 'live' | 'sandbox';
  sellerEntityId: string | null;
  entityHasFiscal: boolean;
  configured: boolean;
  certificateSent: boolean;
  diffs: { field: string; entity: string | null; account: string | null }[];
};
export const adminGetAsaasFiscalInfoCallable = callable<typeof AsaasFiscalInfoModeInputSchema, AsaasFiscalStatus>(
  'adminGetAsaasFiscalInfo',
  AsaasFiscalInfoModeInputSchema,
);
export const adminSyncAsaasFiscalInfoCallable = callable<typeof AsaasFiscalInfoModeInputSchema, AsaasFiscalStatus>(
  'adminSyncAsaasFiscalInfo',
  AsaasFiscalInfoModeInputSchema,
);
/**
 * Resposta de `adminGetAsaasFiscalCatalog` (`apps/functions/src/admin/sellerEntities.ts`):
 * a lista do Asaas para os dropdowns do serviço municipal e dos regimes (#756,
 * 2026-10-07 — eram campos livres).
 */
export type AsaasFiscalCatalog = {
  mode: 'live' | 'sandbox';
  services: { id: string; name: string; issTax: number | null }[];
  specialTaxRegimes: { value: string; label: string }[];
  nationalPortalRegimes: { value: string; label: string }[];
};
export const adminGetAsaasFiscalCatalogCallable = callable<typeof AsaasMunicipalServicesInputSchema, AsaasFiscalCatalog>(
  'adminGetAsaasFiscalCatalog',
  AsaasMunicipalServicesInputSchema,
);
// Sprint 40 — catálogo aberto de PSP (aba PSP de /countries).
export const setPspConfigCallable = callable('setPspConfig', SetPspConfigInput);
export const deletePspConfigCallable = callable('deletePspConfig', DeletePspConfigInput);
// Sprint 35 PR G1 — CRUD super-admin de add-ons (SKU livre via regex).
export type CreateAddonOutput = {
  sku: string;
  paused: boolean;
  stripe: { monthly: string; yearly: string };
};
export const createAddonCallable = callable<typeof CreateAddonInputSchema, CreateAddonOutput>(
  'createAddon',
  CreateAddonInputSchema,
);
// #935 (item 8): saída tipada — o `countries_applied` diz se o servidor gravou
// os países na mesma escrita (servidor antigo não ecoa).
export const updateAddonCallable = callable<typeof UpdateAddonInputSchema, UpdateAddonOutput>(
  'updateAddon',
  UpdateAddonInputSchema,
);
export const setAddonEnabledCountriesCallable = callable(
  'setAddonEnabledCountries',
  SetAddonEnabledCountriesInputSchema,
);
export const disableAddonCallable = callable(
  'disableAddon',
  DisableAddonInputSchema,
);
// #679 D20 — retoma a venda de um add-on pausado.
export const enableAddonCallable = callable(
  'enableAddon',
  EnableAddonInputSchema,
);
// Sprint 35 PR G3/G4 — concede/encerra cortesia. Desde a F7 da #679 é tipo
// comercial (`commercial_mode: COURTESY`), sem cupom nem assinatura Stripe.
export const applyLifetimeFreeCallable = callable(
  'applyLifetimeFree',
  ApplyLifetimeFreeInput,
);
export const removeLifetimeFreeCallable = callable(
  'removeLifetimeFree',
  RemoveLifetimeFreeInput,
);
// #679 F7 (#738) — migração da cortesia/demo em etapas
// (`/billing/courtesy-migration`). A saída do audit e da etapa é validada pelos
// schemas de `courtesyMigration.ts` no componente.
export const auditCourtesyMigrationCallable = callable('auditCourtesyMigration', z.object({}));
export const migrateCourtesyCallable = callable('migrateCourtesy', MigrateCourtesyInput);
export const restoreLegacyCourtesySubscriptionCallable = callable(
  'restoreLegacyCourtesySubscription',
  RestoreLegacyCourtesyInput,
);
// #743 (9.18) — matriz de transições de tipo comercial (parceiro ↔ direto ↔ cortesia).
export const changeTenantCommercialModeCallable = callable<
  typeof ChangeTenantCommercialModeInput,
  ChangeTenantCommercialModeOutput
>('changeTenantCommercialMode', ChangeTenantCommercialModeInput);
export const cancelCommercialModeTransitionCallable = callable(
  'cancelCommercialModeTransition',
  CancelCommercialModeTransitionInput,
);
/**
 * #679 9.7 — fila "Vendas sem pagamento" (`/billing/pending`). A saída é
 * validada pelo `AdminListUnpaidSalesOutput` no componente.
 */
const AdminListUnpaidSalesInputSchema = z.object({});
export const adminListUnpaidSalesCallable = callable<typeof AdminListUnpaidSalesInputSchema>(
  'adminListUnpaidSales',
  AdminListUnpaidSalesInputSchema,
);

// Auditoria §3.4.1 (#13) — categoriza o motivo de cancelamento de um evento
// TENANT_SUSPENDED (/audit/{auditId}) a partir do dashboard /churn. Substitui
// a escrita direta (quebrada) no Firestore. Server revalida + carimba
// cancellationCategorizedAt com server timestamp.
export type CategorizeChurnReasonOutput = {
  auditId: string;
  reason: 'USER_REQUEST' | 'PAYMENT_FAILED' | 'INACTIVITY' | 'UNKNOWN';
  previousReason: 'USER_REQUEST' | 'PAYMENT_FAILED' | 'INACTIVITY' | 'UNKNOWN' | null;
};
export const categorizeChurnReasonCallable = callable<
  typeof CategorizeChurnReasonInputSchema,
  CategorizeChurnReasonOutput
>('categorizeChurnReason', CategorizeChurnReasonInputSchema);

// Sprint 35 PR H1 — export CSV de /audit/billing filtrado.
export type ExportAuditBillingOutput = {
  csv: string;
  rows: number;
  generatedAt: number;
};
export const exportAuditBillingCallable = callable<
  typeof ExportAuditBillingInputSchema,
  ExportAuditBillingOutput
>('exportAuditBilling', ExportAuditBillingInputSchema);
// Sprint 5 PR F — super-admin emite refund manual de um pagamento
// (parcial ou total). Desde a #679 F4 (9.16) é o ÚNICO caminho de reembolso:
// excluir o estabelecimento não reembolsa mais nada sozinho — o comercial pede
// e o super-admin executa aqui.
export type RequestRefundOutput = {
  refundId: string;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED';
  externalRefundId: string | null;
  amount: number;
  currency: string;
};
export const requestRefundCallable = callable<
  typeof RequestRefundInput,
  RequestRefundOutput
>('requestRefund', RequestRefundInput);

// Cancela a assinatura no gateway no fim do período pago: o cliente fica até
// `scheduledUntil` e o webhook do gateway corta (#961). Sem período a preservar
// (gateway já encerrado / sem gateway), corta na hora e limpa os marcadores de
// "pago" → paywall (`scheduledUntil: null`).
export type CancelSubscriptionAdminOutput = {
  subscriptionCancelled: boolean;
  invoicesVoided: number;
  gatewayCancelled: boolean | null;
  /** Epoch ms do fim do acesso quando o cancelamento foi agendado (#961). */
  scheduledUntil: number | null;
};
export const cancelSubscriptionAdminCallable = callable<
  typeof CancelSubscriptionAdminInput,
  CancelSubscriptionAdminOutput
>('cancelSubscriptionAdmin', CancelSubscriptionAdminInput);

// Prorroga o próximo vencimento da assinatura no PSP (Stripe trial_end):
// empurra o current_period_end para a data escolhida sem cobrar o intervalo.
export type PostponeRenewalAdminOutput = {
  tenantId: string;
  newPeriodEnd: number;
};
export const postponeRenewalAdminCallable = callable<
  typeof PostponeRenewalAdminInput,
  PostponeRenewalAdminOutput
>('postponeRenewalAdmin', PostponeRenewalAdminInput);

// Reconcilia o doc billing/subscription com a verdade do Stripe — ferramenta
// de ops para corrigir docs dessincronizados (re-checkout que deixou estado
// stale sobre uma sub viva). Input mínimo: tenantId.
const ReconcileSubscriptionInput = z.object({ tenantId: z.string().min(1) });
export type ReconcileSubscriptionOutput = {
  ok: boolean;
  tenantId: string;
  reconciled: boolean;
  subscriptionId?: string;
  status?: string;
  billing_cycle?: string;
  paywalled?: boolean;
  /** Quantos `Payment` docs test-mode (livemode:false) foram removidos. */
  removedTestPayments?: number;
};
export const reconcileSubscriptionFromGatewayCallable = callable<
  typeof ReconcileSubscriptionInput,
  ReconcileSubscriptionOutput
>('reconcileSubscriptionFromGateway', ReconcileSubscriptionInput);

// #289 — link de pagamento. O console tem callables próprios para gerar,
// listar e reenviar o link de quem abandonou o checkout: o
// `createCheckoutSession` da época exigia ser OWNER do tenant (e saiu do
// servidor em 2026-09-24, #714).
export const adminCreateCheckoutLinkCallable = callable<
  typeof AdminCreateCheckoutLinkInput,
  AdminCreateCheckoutLinkOutput
>('adminCreateCheckoutLink', AdminCreateCheckoutLinkInput);

export const adminGetCheckoutLinksCallable = callable<
  typeof AdminGetCheckoutLinksInput,
  AdminGetCheckoutLinksOutput
>('adminGetCheckoutLinks', AdminGetCheckoutLinksInput);

export const adminSendCheckoutLinkEmailCallable = callable<
  typeof AdminSendCheckoutLinkEmailInput,
  AdminSendCheckoutLinkEmailOutput
>('adminSendCheckoutLinkEmail', AdminSendCheckoutLinkEmailInput);

// Sprint 15 PR D — Label templates (CRUD + seed). Server revalida via Zod;
// retorno carrega `templateId` (callables CUD) ou contadores (seed/reset).
export type LabelTemplateMutationOutput = { templateId: string };
export const createLabelTemplateCallable = callable<
  typeof CreateLabelTemplateInput,
  LabelTemplateMutationOutput
>('createLabelTemplate', CreateLabelTemplateInput);
export const updateLabelTemplateCallable = callable<
  typeof UpdateLabelTemplateInput,
  LabelTemplateMutationOutput
>('updateLabelTemplate', UpdateLabelTemplateInput);
export const deleteLabelTemplateCallable = callable<
  typeof DeleteLabelTemplateInput,
  LabelTemplateMutationOutput
>('deleteLabelTemplate', DeleteLabelTemplateInput);
export const setDefaultLabelTemplateCallable = callable<
  typeof SetDefaultLabelTemplateInput,
  LabelTemplateMutationOutput
>('setDefaultLabelTemplate', SetDefaultLabelTemplateInput);

export type SeedLabelTemplatesOutput = { seeded: boolean; count: number };
export const seedDefaultLabelTemplatesForExistingTenantCallable = callable<
  typeof SeedDefaultLabelTemplatesForExistingTenantInput,
  SeedLabelTemplatesOutput
>('seedDefaultLabelTemplatesForExistingTenant', SeedDefaultLabelTemplatesForExistingTenantInput);

export type ResetLabelTemplatesOutput = { deletedCount: number; recreatedCount: number };
export const resetDefaultLabelTemplatesCallable = callable<
  typeof SeedDefaultLabelTemplatesForExistingTenantInput,
  ResetLabelTemplatesOutput
>('resetDefaultLabelTemplates', SeedDefaultLabelTemplatesForExistingTenantInput);

// listDrilldown — chamado pelo DrilldownModal (Sprint 4.9). Schema
// vive no server (Zod), aqui usamos tipos enxutos pra não acoplar.
// Em 2026-10-10 o `DrilldownModal` não existe mais no admin e este wrapper não
// tem consumidor (achado da #926).
export type ListDrilldownInput = {
  metric: string;
  yyyymmdd: string;
  filter?: { countryCode?: string; currency?: 'BRL' | 'USD' | 'EUR' };
  limit?: number;
};
export type ListDrilldownRow = {
  tenantId: string;
  tenantName: string;
  countryCode: string;
  currency: 'BRL' | 'USD' | 'EUR' | null;
  contributionUsdCents: number | null;
  rawValue: number | null;
};
export type ListDrilldownResult = { rows: ListDrilldownRow[] };
const listDrilldownFn: HttpsCallable<ListDrilldownInput, ListDrilldownResult> =
  httpsCallable(functions, 'listDrilldown');
/**
 * Lista os tenants que compõem um ponto de uma métrica agregada (drilldown).
 *
 * Origem: sem issue — commit c00f00729, 2026-05-04 (DrilldownModal do BI).
 *
 * Contrato: sem validação Zod no cliente (o schema mora só no servidor,
 * `apps/functions/src/aggregations/listDrilldown.ts`); lança o `FirebaseError`.
 *
 * Usado por: nenhum consumidor no `apps/admin` em 2026-10-10 (#926).
 */
export async function listDrilldownCallable(
  input: ListDrilldownInput,
): Promise<ListDrilldownResult> {
  assertOnline();
  console.info(`${TAG}/listDrilldown invoked`);
  try {
    const result = await listDrilldownFn(input);
    console.info(`${TAG}/listDrilldown success`);
    return result.data;
  } catch (err) {
    reportCallableFailure('listDrilldown', err);
    throw err;
  }
}

// verifyAdminLogin não tem input — wrapper direto sem parse Zod, com os
// mesmos logs invoked/success/failed dos outros.
export type VerifyAdminLoginResult = { ok: true; uid: string; email: string; role: AdminRole };
const verifyAdminLoginFn: HttpsCallable<undefined, VerifyAdminLoginResult> = httpsCallable(
  functions,
  'verifyAdminLogin',
);
/**
 * Revalida no servidor se o usuário logado ainda é admin.
 *
 * Origem: sem issue — commit eebbfc524, 2026-04-26 (callables
 * superAdmin/catalog/verifyLogin, Sprint 2).
 *
 * Contrato: sem input; devolve `{ ok, uid, email, role }`. Lança
 * `functions/permission-denied` quando o servidor recusa — único caso em que
 * o `AuthProvider` desloga.
 *
 * Usado por: `auth/AuthProvider.tsx` (após cada troca de token autorizada).
 *
 * Arquitetura: Function `apps/functions/src/admin/verifyAdminLogin.ts`.
 */
export async function verifyAdminLoginCallable(): Promise<VerifyAdminLoginResult> {
  assertOnline();
  console.info(`${TAG}/verifyAdminLogin invoked`);
  try {
    const result = await verifyAdminLoginFn();
    console.info(`${TAG}/verifyAdminLogin success`);
    return result.data;
  } catch (err) {
    reportCallableFailure('verifyAdminLogin', err);
    throw err;
  }
}

// Web push (FCM) do console super-admin — registra/desregistra o token do
// navegador em `/adminPushTokens/{token}`. Chamado pelo `webPushController`
// (toggle de notificações em /settings). O fan-out existe desde 6d8cac847
// (2026-06-19): `notifyAdmins` em `apps/functions/src/lib/adminNotify.ts`
// (ex.: `onTenantCreated`) envia push via `lib/webPush.ts`. (#926: o
// comentário anterior dizia que nenhum evento fazia fan-out.)
export const registerAdminPushTokenCallable = callable<
  typeof RegisterAdminPushTokenInput,
  { registered: true }
>('registerAdminPushToken', RegisterAdminPushTokenInput);

export const unregisterAdminPushTokenCallable = callable<
  typeof UnregisterAdminPushTokenInput,
  { unregistered: true }
>('unregisterAdminPushToken', UnregisterAdminPushTokenInput);

// Broadcast manual super-admin para todos os OWNERs. Multicast sobre os tokens
// em Firestore desde a #767 (o tópico FCM usava a InstanceId API, desligada em
// 2027-09-29) — por isso a resposta traz contagens e não mais `messageId`.
// `sent`/`failed` são o veredito do FCM por token, não exibição no aparelho.
// As contagens são opcionais no tipo porque, na janela de deploy (Hosting sobe
// antes das Functions), a Function antiga ainda responde `{ messageId, sentAt }`.
export type BroadcastToOwnersOutput = {
  sentAt: number;
  owners?: number;
  tokens?: number;
  sent?: number;
  failed?: number;
  pruned?: number;
};
export const broadcastToOwnersCallable = callable<
  typeof BroadcastToOwnersInput,
  BroadcastToOwnersOutput
>('broadcastToOwners', BroadcastToOwnersInput);

// --- Gestão de usuários e responsáveis do tenant (painel super-admin).
// Os callables foram estendidos no backend para aceitar "super-admin OU OWNER"
// (ver apps/functions/src/auth/isSuperAdminRequest.ts). Aqui só ligamos os
// wrappers. Desde a #776 `removeUserFromTenant` e `updateUserTenantRole` usam o
// schema de @etiquetachef/types (era inline aqui e no servidor).
export const inviteUserToTenantCallable = callable<
  typeof InviteUserToTenantInput,
  { uid: string; accountCreated: boolean }
>('inviteUserToTenant', InviteUserToTenantInput);

export const createStaffWithCredentialsCallable = callable<
  typeof CreateStaffWithCredentialsInput,
  { uid: string }
>('createStaffWithCredentials', CreateStaffWithCredentialsInput);

export const updateUserTenantRoleCallable = callable<
  typeof UpdateUserTenantRoleInput,
  { changed: boolean }
>('updateUserTenantRole', UpdateUserTenantRoleInput);

export const removeUserFromTenantCallable = callable<
  typeof RemoveUserFromTenantInput,
  { removed: true }
>('removeUserFromTenant', RemoveUserFromTenantInput);

export const setMemberPasswordCallable = callable<
  typeof SetMemberPasswordInput,
  { changed: true }
>('setMemberPassword', SetMemberPasswordInput);

export const setStaffPinCallable = callable<typeof SetStaffPinInput, { changed: true }>(
  'setStaffPin',
  SetStaffPinInput,
);

export type CreateResponsibleOutput = {
  responsibleId: string;
  createdAtMillis: number;
  becameDefault: boolean;
};
export const createResponsibleCallable = callable<
  typeof CreateResponsibleInputSchema,
  CreateResponsibleOutput
>('createResponsible', CreateResponsibleInputSchema);

export const setResponsibleActiveCallable = callable<
  typeof SetResponsibleActiveInputSchema,
  { ok: true; promotedDefaultId: string | null }
>('setResponsibleActive', SetResponsibleActiveInputSchema);

export const setResponsibleDefaultCallable = callable<
  typeof SetResponsibleDefaultInputSchema,
  { ok: true }
>('setResponsibleDefault', SetResponsibleDefaultInputSchema);

export const deleteResponsibleCallable = callable<
  typeof DeleteResponsibleInputSchema,
  { ok: true; promotedDefaultId: string | null }
>('deleteResponsible', DeleteResponsibleInputSchema);

// Backfill manual das agregações dependentes do PostHog (posthog/funnel):
// reprocessa um intervalo de dias para preencher gaps de "—" deixados por
// falhas da Query API no horário do scheduler. Super-admin only.
export const backfillAggregationsCallable = callable<
  typeof BackfillAggregationsInput,
  BackfillAggregationsOutput
>('backfillAggregations', BackfillAggregationsInput);

// Financeiro interno da empresa (épico #72, PR A #73) — CRUD de contas.
// Super-admin only; coleção root /finance_accounts.
export const createFinanceAccountCallable = callable<
  typeof CreateFinanceAccountInputSchema,
  CreateFinanceAccountOutput
>('createFinanceAccount', CreateFinanceAccountInputSchema);
export const updateFinanceAccountCallable = callable<
  typeof UpdateFinanceAccountInputSchema,
  UpdateFinanceAccountOutput
>('updateFinanceAccount', UpdateFinanceAccountInputSchema);
export const archiveFinanceAccountCallable = callable<
  typeof ArchiveFinanceAccountInputSchema,
  ArchiveFinanceAccountOutput
>('archiveFinanceAccount', ArchiveFinanceAccountInputSchema);

// PR B #74 — lançamentos do extrato interno.
export const createFinanceEntryCallable = callable<
  typeof CreateFinanceEntryInputSchema,
  CreateFinanceEntryOutput
>('createFinanceEntry', CreateFinanceEntryInputSchema);
export const updateFinanceEntryCallable = callable<
  typeof UpdateFinanceEntryInputSchema,
  UpdateFinanceEntryOutput
>('updateFinanceEntry', UpdateFinanceEntryInputSchema);
export const deleteFinanceEntryCallable = callable<
  typeof DeleteFinanceEntryInputSchema,
  DeleteFinanceEntryOutput
>('deleteFinanceEntry', DeleteFinanceEntryInputSchema);
export const searchFinanceEntriesCallable = callable<
  typeof SearchFinanceEntriesInputSchema,
  SearchFinanceEntriesResult
>('searchFinanceEntries', SearchFinanceEntriesInputSchema);

// PR E #77 — tags livres: lista as tags já usadas (autocomplete + filtros).
export const listFinanceTagsCallable = callable<
  typeof ListFinanceTagsInputSchema,
  ListFinanceTagsResult
>('listFinanceTags', ListFinanceTagsInputSchema);

// PR D #76 — análise: saldo/balanço e fluxo de caixa.
export const computeFinanceBalanceCallable = callable<
  typeof ComputeFinanceBalanceInputSchema,
  ComputeFinanceBalanceResult
>('computeFinanceBalance', ComputeFinanceBalanceInputSchema);
export const computeCashflowCallable = callable<
  typeof ComputeCashflowInputSchema,
  ComputeCashflowResult
>('computeCashflow', ComputeCashflowInputSchema);

// #78 — recebíveis de assinaturas (Cobrança → "Assinaturas"): série semanal
// previsto × realizado × em aberto, do início do app às renovações futuras.
export const computeSubscriptionReceivablesCallable = callable<
  typeof ComputeSubscriptionReceivablesInputSchema,
  ComputeSubscriptionReceivablesResult
>('computeSubscriptionReceivables', ComputeSubscriptionReceivablesInputSchema);

// PR C #75 — import por OCR/LLM de extrato. parse devolve preview (sem
// gravar); confirm grava os confirmados em lote e fecha a sessão.
export const parseBankStatementImageCallable = callable<
  typeof ParseBankStatementImageInputSchema,
  ParseBankStatementImageOutput
>('parseBankStatementImage', ParseBankStatementImageInputSchema);
export const confirmStatementImportCallable = callable<
  typeof ConfirmStatementImportInputSchema,
  ConfirmStatementImportOutput
>('confirmStatementImport', ConfirmStatementImportInputSchema);

// #263 — integrações da API pública readonly (/settings/api-keys). O
// `clientSecret` só existe no retorno de `adminCreateApiKey`: a UI mostra uma
// vez e o valor some do sistema.
export const adminCreateApiKeyCallable = callable<
  typeof CreateApiKeyInputSchema,
  CreateApiKeyResult
>('adminCreateApiKey', CreateApiKeyInputSchema);
export const adminUpdateApiKeyCallable = callable(
  'adminUpdateApiKey',
  UpdateApiKeyInputSchema,
);
export const adminRevokeApiKeyCallable = callable(
  'adminRevokeApiKey',
  RevokeApiKeyInputSchema,
);
// #791 — remoção definitiva; o servidor só aceita integração já revogada.
export const adminDeleteApiKeyCallable = callable(
  'adminDeleteApiKey',
  DeleteApiKeyInputSchema,
);
// O `clientSecret` não é recuperável (o banco guarda só o sha256): quem perdeu
// rotaciona, e o novo segredo aparece uma única vez neste retorno.
export const adminRotateApiKeySecretCallable = callable<
  typeof RotateApiKeySecretInputSchema,
  RotateApiKeySecretResult
>('adminRotateApiKeySecret', RotateApiKeySecretInputSchema);
